/**
 * Moving a note that is already written, rather than writing a new one.
 *
 * Typing a letter says which note to write; the arrows say the note is nearly right. They
 * are two different operations on the same state, and this one is here rather than in the
 * entry machine because it is the only part of note entry that does not involve the caret's
 * duration, the bar's capacity or what to do with the silence around it -- it changes
 * pitches and nothing else.
 *
 * Two kinds of move, because a keyboard has two questions to ask. A semitone keeps the
 * letter and writes an accidental, which is what a player means by "sharpen this"; a letter
 * step keeps the accidental and moves the head to the next line or space, which is what a
 * player means by "no, the one above". Seven of the latter is an octave.
 *
 * On a tab staff a move is still a move in pitch, and the hand follows it: the string stays
 * and the fret slides, because that is what a player does to raise a note by a semitone.
 * Only when the slide runs off the end of the neck does the note change string.
 */

import type { Command } from "../commands/types";
import type { EventId } from "../document/ids";
import { STEPS, diatonicOf, drumHeadOf, midiOf, spell } from "../document/pitch";
import { drumOf, drumStepped } from "../document/percussion";
import { fretFor, positionFor } from "../document/tab";
import type { StringTuning } from "../document/tab";
import type { NotePitch } from "../document/pitch";
import { locateEvent, resolveAttributes } from "../document/query";
import type { Score } from "../document/types";
import { indexAt, placeOf } from "./entry-place";
import type { Caret } from "./entry-place";

/**
 * How far to move a note, and in which currency.
 *
 * `semitone` keeps the letter and writes an accidental; `step` keeps the accidental and
 * moves the head to the next line or space. A count of seven steps is an octave.
 */
export interface PitchMove {
  readonly by: "semitone" | "step";
  readonly count: number;
}

/** The maximum an accidental can say: a double sharp or a double flat. */
const LIMIT = 2;

/**
 * A pitch a number of semitones away, keeping its letter.
 *
 * Past a double sharp or a double flat the letter has to give way -- there is no triple
 * sharp to write -- and the note is respelled the plainest way the key allows, which is the
 * same rule a pitch arriving without a spelling is given.
 */
export function altered(pitch: NotePitch, semitones: number, fifths: number): NotePitch {
  const alter = pitch.alter + semitones;
  if (Math.abs(alter) <= LIMIT) return { ...pitch, alter, accidental: "auto" };
  const plain = spell(midiOf(pitch) + semitones, fifths);
  return { ...pitch, step: plain.step, alter: plain.alter, octave: plain.octave };
}

/** A pitch a number of letters away, keeping whatever accidental it has. */
export function shifted(pitch: NotePitch, steps: number): NotePitch {
  const ladder = diatonicOf(pitch) + steps;
  return {
    ...pitch,
    step: STEPS[((ladder % 7) + 7) % 7] ?? pitch.step,
    octave: Math.floor(ladder / 7),
  };
}

/**
 * The moved note's place on the neck: the same string at another fret where it reaches.
 *
 * A note that had no place yet is given one, so that a pitch arriving from somewhere with
 * no tab of its own -- a paste, an imported file -- can still be moved on a tab staff.
 */
export function refretted(
  tuning: readonly StringTuning[],
  was: NotePitch,
  now: NotePitch,
): NotePitch {
  const string = was.tab?.string ?? null;
  const fret = string === null ? null : fretFor(tuning, string, now);
  const tab = string !== null && fret !== null ? { string, fret } : positionFor(tuning, now);
  return { ...now, tab };
}

/** A note moved: the edit to apply, and what it now reads as. */
export interface Repitched {
  readonly command: Command;
  readonly pitches: readonly NotePitch[];
}

/**
 * Move every head of one event, or say why it cannot be moved.
 *
 * A chord moves as a chord: the alternative is a rule about which of its notes the arrows
 * mean, and there is no such rule that is not a surprise. A note in a transposing part is
 * moved as it is written, because the arrows act on the page the player reads.
 */
export function repitch(score: Score, target: EventId, move: PitchMove): Repitched | string {
  const at = locateEvent(score, target);
  if (at === null) return "that note is no longer in the score";
  if (at.event.kind !== "note") return "a rest has no pitch to move";

  const { key } = resolveAttributes(score, at.measureIndex);
  const instrument = score.parts.find((part) => part.id === at.partId)?.instrument ?? null;
  const kit = instrument?.percussion ?? null;
  const tuning = instrument?.tuning ?? null;
  const step = move.by === "semitone" ? Math.sign(move.count) : move.count;
  const pitches = at.event.pitches.map((pitch) => {
    const drum = drumOf(kit, pitch.drum);
    // A drum has no pitch to raise: both arrows mean the next drum along the staff, and a
    // semitone is one place rather than none.
    if (drum !== null && kit !== null) {
      const moved = drumStepped(kit, drum, step);
      return {
        ...drumHeadOf(moved.step, moved.octave, moved.name),
        tieStart: pitch.tieStart,
        tieStop: pitch.tieStop,
      };
    }
    const moved =
      move.by === "semitone" ? altered(pitch, move.count, key.fifths) : shifted(pitch, move.count);
    return tuning === null ? moved : refretted(tuning, pitch, moved);
  });
  return { command: { kind: "setPitches", eventId: target, pitches }, pitches };
}

/**
 * The note an arrow acts on: the one just written, or the one the caret stands on.
 *
 * Writing a note steps the caret past it, so after typing `G` the caret is already at the
 * next moment and an arrow that read the caret would sharpen the wrong note -- or nothing
 * at all. The note last written is therefore preferred, which also makes a run of arrows
 * act on the same note rather than walking away from it.
 */
export function targetEvent(
  score: Score,
  caret: Caret | null,
  last: EventId | null,
): EventId | null {
  if (last !== null) return last;
  if (caret === null) return null;
  const place = placeOf(score, caret);
  if (typeof place === "string") return null;
  return place.voice.events[indexAt(place.voice, caret.onset, place.capacity)]?.id ?? null;
}
